import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import "./ExportOptionsModal.css";

export default function ExportOptionsModal({
  open,
  charts = [],
  loading = false,
  onClose,
  onExport,
}) {
  const [format, setFormat] = useState("pptx");
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState("");

  const chartIds = useMemo(() => charts.map((chart) => chart.id), [charts]);
  const allSelected = chartIds.length > 0 && selected.length === chartIds.length;

  useEffect(() => {
    if (!open) return;
    setFormat("pptx");
    setSelected(chartIds);
    setError("");
  }, [open, chartIds]);

  useEffect(() => {
    if (!open) return undefined;

    function handleKeyDown(event) {
      if (event.key === "Escape" && !loading) onClose?.();
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, loading, onClose]);

  if (!open || typeof document === "undefined") return null;

  function toggleChart(chartId) {
    setError("");
    setSelected((prev) =>
      prev.includes(chartId)
        ? prev.filter((id) => id !== chartId)
        : [...prev, chartId]
    );
  }

  function selectAll() {
    setError("");
    setSelected(chartIds);
  }

  function clearAll() {
    setSelected([]);
    setError("Selecione pelo menos 1 gráfico para exportar.");
  }

  function handleSubmit(event) {
    event.preventDefault();

    if (!selected.length) {
      setError("Selecione pelo menos 1 gráfico para exportar.");
      return;
    }

    const selectedTitles = charts
      .filter((chart) => selected.includes(chart.id))
      .map((chart) => chart.title);

    onExport?.({ format, selectedTitles });
  }

  return createPortal(
    <div className="exportModal" role="presentation">
      <button
        type="button"
        className="exportModal__backdrop"
        aria-label="Fechar exportação"
        onClick={loading ? undefined : onClose}
      />

      <form
        className="exportModal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-modal-title"
        onSubmit={handleSubmit}
      >
        <div className="exportModal__head">
          <div>
            <h2 id="export-modal-title" className="exportModal__title">
              Exportar relatório
            </h2>
            <p className="exportModal__subtitle">
              Escolha o modelo e os gráficos do arquivo.
            </p>
          </div>

          <button
            type="button"
            className="exportModal__close"
            onClick={onClose}
            disabled={loading}
            aria-label="Fechar"
          >
            ×
          </button>
        </div>

        <div className="exportModal__section">
          <div className="exportModal__label">Modelo</div>
          <div className="exportModal__formatGroup">
            <label className="exportModal__format">
              <input
                type="radio"
                name="export-format"
                value="pptx"
                checked={format === "pptx"}
                onChange={() => setFormat("pptx")}
              />
              <span>PPTX</span>
            </label>

            <label className="exportModal__format">
              <input
                type="radio"
                name="export-format"
                value="pdf"
                checked={format === "pdf"}
                onChange={() => setFormat("pdf")}
              />
              <span>PDF</span>
            </label>
          </div>
        </div>

        <div className="exportModal__section">
          <div className="exportModal__toolbar">
            <div>
              <div className="exportModal__label">Gráficos com dados</div>
              <div className="exportModal__count">
                {selected.length}/{chartIds.length} selecionados
              </div>
            </div>

            <div className="exportModal__bulk">
              <button type="button" onClick={selectAll} disabled={loading || allSelected}>
                Selecionar todos
              </button>
              <button type="button" onClick={clearAll} disabled={loading || !selected.length}>
                Remover todos
              </button>
            </div>
          </div>

          {charts.length ? (
            <div className="exportModal__list">
              {charts.map((chart) => (
                <label className="exportModal__option" key={chart.id}>
                  <input
                    type="checkbox"
                    checked={selected.includes(chart.id)}
                    onChange={() => toggleChart(chart.id)}
                  />
                  <span>{chart.title}</span>
                </label>
              ))}
            </div>
          ) : (
            <div className="exportModal__empty">
              Nenhum gráfico disponível para exportação.
            </div>
          )}

          {error ? <div className="exportModal__error">{error}</div> : null}
        </div>

        <div className="exportModal__actions">
          <button
            type="button"
            className="exportModal__secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="exportModal__primary"
            disabled={loading || !charts.length}
          >
            {loading ? "Exportando..." : "Exportar"}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
